import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextLazy';
import { supabase } from '../../lib/supabase-auth';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FolderIcon,
  UserIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ChevronRightIcon,
  FunnelIcon,
  PlusIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { colors } from '../../lib/colors';

// Status configuration
const PIPELINE_STATUSES = [
  { id: 'proposal', label: 'Proposal', color: 'bg-amber-500', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
  { id: 'engaged', label: 'Engaged', color: 'bg-blue-500', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-indigo-500', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
  { id: 'on_hold', label: 'On Hold', color: 'bg-gray-500', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' },
  { id: 'complete', label: 'Complete', color: 'bg-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
];

interface ProjectParty {
  party_id: string;
  role: string;
  is_primary: boolean;
  party: {
    id: string;
    name: string;
    type: string;
  };
}

interface Project {
  id: string;
  project_number: string | null;
  name: string;
  description: string | null;
  status: string;
  project_type: string | null;
  current_phase: number;
  total_phases: number;
  fee_total: number | null;
  fee_paid: number | null;
  tasks_total: number;
  tasks_completed: number;
  target_completion_date: string | null;
  created_at: string;
  assigned_to: string | null;
  assignee?: {
    id: string;
    full_name: string;
  };
  project_parties?: ProjectParty[];
}

interface PipelineColumn {
  id: string;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  projects: Project[];
}

// Sortable Project Card Component
function SortableProjectCard({ project, onClick }: { project: Project; onClick: () => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ProjectCard project={project} onClick={onClick} isDragging={isDragging} />
    </div>
  );
}

// Project Card Component
function ProjectCard({ project, onClick, isDragging = false }: { project: Project; onClick: () => void; isDragging?: boolean }) {
  const primaryParty = project.project_parties?.find(pp => pp.is_primary)?.party;
  const progress = project.tasks_total > 0
    ? Math.round((project.tasks_completed / project.tasks_total) * 100)
    : 0;
  const paymentProgress = project.fee_total && project.fee_total > 0
    ? Math.round(((project.fee_paid || 0) / project.fee_total) * 100)
    : null;

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-lg border border-gray-200 p-4 cursor-pointer
        hover:shadow-md hover:border-gray-300 transition-all duration-200
        ${isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''}
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 mb-1">
            {project.project_number || 'No ID'}
          </p>
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {project.name}
          </h4>
        </div>
        <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
      </div>

      {/* Client */}
      {primaryParty && (
        <div className="flex items-center gap-2 mb-3">
          <UserIcon className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600 truncate">{primaryParty.name}</span>
        </div>
      )}

      {/* Phase Progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">
            Phase {project.current_phase} of {project.total_phases}
          </span>
          <span className="text-xs font-medium text-gray-700">{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Task Progress */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
        <span>{project.tasks_completed}/{project.tasks_total} tasks</span>
        {project.target_completion_date && (
          <span className="flex items-center gap-1">
            <CalendarDaysIcon className="h-3 w-3" />
            {new Date(project.target_completion_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>

      {/* Payment Status (if applicable) */}
      {paymentProgress !== null && (
        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <CurrencyDollarIcon className="h-3 w-3" />
              Payment
            </span>
            <span className={`text-xs font-medium ${paymentProgress >= 100 ? 'text-green-600' : 'text-amber-600'}`}>
              {paymentProgress}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Droppable Column Component
function PipelineColumn({ column, onProjectClick }: { column: PipelineColumn; onProjectClick: (id: string) => void }) {
  return (
    <div className="flex flex-col min-w-[300px] max-w-[300px]">
      {/* Column Header */}
      <div className={`rounded-t-lg px-4 py-3 ${column.bgColor} border ${column.borderColor} border-b-0`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${column.color}`} />
            <h3 className="font-semibold text-gray-900">{column.label}</h3>
          </div>
          <span className="text-sm font-medium text-gray-500 bg-white px-2 py-0.5 rounded-full">
            {column.projects.length}
          </span>
        </div>
      </div>

      {/* Column Content */}
      <div className={`flex-1 p-3 bg-gray-50 border-x ${column.borderColor} min-h-[500px] max-h-[calc(100vh-280px)] overflow-y-auto`}>
        <SortableContext
          items={column.projects.map(p => p.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {column.projects.map((project) => (
              <SortableProjectCard
                key={project.id}
                project={project}
                onClick={() => onProjectClick(project.id)}
              />
            ))}
            {column.projects.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <FolderIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No projects</p>
              </div>
            )}
          </div>
        </SortableContext>
      </div>

      {/* Column Footer */}
      <div className={`rounded-b-lg px-4 py-2 border ${column.borderColor} border-t-0 bg-gray-50`}>
        <p className="text-xs text-gray-500 text-center">
          {column.projects.length === 1 ? '1 project' : `${column.projects.length} projects`}
        </p>
      </div>
    </div>
  );
}

// Main Component
export const EngagementsPipeline: React.FC = () => {
  const navigate = useNavigate();
  const { organization, userProfile, hasRole } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filterAdvisor, setFilterAdvisor] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [advisors, setAdvisors] = useState<{ id: string; full_name: string }[]>([]);
  const [projectTypes, setProjectTypes] = useState<string[]>([]);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    if (!organization) return;

    try {
      setLoading(true);

      // Build query
      let query = supabase
        .from('projects')
        .select('id,project_number,name,description,status,project_type,current_phase,total_phases,fee_total,fee_paid,tasks_total,tasks_completed,target_completion_date,created_at,assigned_to,assignee:platform_users!assigned_to(id,full_name),project_parties(party_id,role,is_primary,party:parties(id,name,type))')
        .eq('organization_id', organization.id)
        .in('status', ['proposal', 'engaged', 'in_progress', 'on_hold', 'complete'])
        .order('created_at', { ascending: false });

      // Apply filters
      if (filterAdvisor !== 'all') {
        query = query.eq('assigned_to', filterAdvisor);
      }
      if (filterType !== 'all') {
        query = query.eq('project_type', filterType);
      }

      const { data, error } = await query;

      if (error) throw error;
      setProjects(data || []);

      // Extract unique advisors and project types for filters
      const uniqueAdvisors = new Map();
      const uniqueTypes = new Set<string>();

      (data || []).forEach((p: Project) => {
        if (p.assignee) {
          uniqueAdvisors.set(p.assignee.id, p.assignee);
        }
        if (p.project_type) {
          uniqueTypes.add(p.project_type);
        }
      });

      setAdvisors(Array.from(uniqueAdvisors.values()));
      setProjectTypes(Array.from(uniqueTypes));
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching projects:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [organization, filterAdvisor, filterType]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Organize projects by status
  const columns: PipelineColumn[] = PIPELINE_STATUSES.map((status) => ({
    ...status,
    projects: projects.filter((p) => p.status === status.id),
  }));

  // Get active project for drag overlay
  const activeProject = activeId ? projects.find((p) => p.id === activeId) : null;

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeProject = projects.find((p) => p.id === active.id);
    if (!activeProject) return;

    // Find the target column/status
    let newStatus: string | null = null;

    // Check if dropped on a column
    const targetColumnId = PIPELINE_STATUSES.find((s) => s.id === over.id)?.id;
    if (targetColumnId) {
      newStatus = targetColumnId;
    } else {
      // Dropped on another card - find its column
      const targetProject = projects.find((p) => p.id === over.id);
      if (targetProject) {
        newStatus = targetProject.status;
      }
    }

    if (newStatus && newStatus !== activeProject.status) {
      // Update status in database
      try {
        const { error } = await supabase
          .from('projects')
          .update({
            status: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', activeProject.id);

        if (error) throw error;

        // Update local state
        setProjects((prev) =>
          prev.map((p) =>
            p.id === activeProject.id ? { ...p, status: newStatus! } : p
          )
        );
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error updating project status:', error);
        }
        // Revert on error - refetch
        fetchProjects();
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Visual feedback handled by DndContext
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/portal/engagements/${projectId}`);
  };

  const handleNewProject = () => {
    navigate('/portal/projects/new');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Engagements Pipeline</h1>
            <p className="text-sm text-gray-500 mt-1">
              Drag projects between columns to update their status
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Filters */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />

              {/* Advisor Filter */}
              <select
                value={filterAdvisor}
                onChange={(e) => setFilterAdvisor(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Advisors</option>
                {advisors.map((advisor) => (
                  <option key={advisor.id} value={advisor.id}>
                    {advisor.full_name}
                  </option>
                ))}
              </select>

              {/* Type Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                {projectTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* Refresh */}
            <button
              onClick={fetchProjects}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className="h-5 w-5" />
            </button>

            {/* New Project */}
            <button
              onClick={handleNewProject}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white shadow-lg transition-all duration-200 hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
                boxShadow: `0 4px 12px -2px ${colors.brand.primary}40`,
              }}
            >
              <PlusIcon className="h-5 w-5 mr-1.5" />
              New Project
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="flex items-center gap-6 mt-4">
          {columns.map((column) => (
            <div key={column.id} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${column.color}`} />
              <span className="text-sm text-gray-600">{column.label}:</span>
              <span className="text-sm font-semibold text-gray-900">{column.projects.length}</span>
            </div>
          ))}
          <div className="ml-auto text-sm text-gray-500">
            Total: <span className="font-semibold text-gray-900">{projects.length}</span> projects
          </div>
        </div>
      </div>

      {/* Pipeline Kanban Board */}
      <div className="flex-1 overflow-x-auto p-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          <div className="flex gap-4 h-full">
            {columns.map((column) => (
              <PipelineColumn
                key={column.id}
                column={column}
                onProjectClick={handleProjectClick}
              />
            ))}
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeProject ? (
              <ProjectCard
                project={activeProject}
                onClick={() => {}}
                isDragging
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default EngagementsPipeline;
